import { useRef, useEffect } from "react";

const UNLOCK_DISTANCE_FROM_BOTTOM_PX = 120;
const LOAD_DISTANCE_FROM_BOTTOM_PX = 80;

/**
 * Scroll-container infinite load — one page per approach to the bottom.
 * Disabled entirely when `enabled` is false (e.g. after a fetch error).
 */
export const useInfiniteScroll = (
	isFetching: boolean,
	hasNextPage: boolean,
	fetchNextPage: () => void,
	scrollRootId?: string,
	enabled: boolean = true,
) => {
	const isFetchingRef = useRef(isFetching);
	isFetchingRef.current = isFetching;

	const hasNextPageRef = useRef(hasNextPage);
	hasNextPageRef.current = hasNextPage;

	const fetchNextPageRef = useRef(fetchNextPage);
	fetchNextPageRef.current = fetchNextPage;

	const enabledRef = useRef(enabled);
	enabledRef.current = enabled;

	const canLoadRef = useRef(false);

	useEffect(() => {
		if (!enabled) {
			canLoadRef.current = false;
		}
	}, [enabled]);

	useEffect(() => {
		const rootEl = scrollRootId ? document.getElementById(scrollRootId) : null;
		if (!rootEl) return;

		const handleScroll = () => {
			if (!enabledRef.current) return;

			const { scrollTop, scrollHeight, clientHeight } = rootEl;
			const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

			if (distanceFromBottom > UNLOCK_DISTANCE_FROM_BOTTOM_PX) {
				canLoadRef.current = true;
				return;
			}

			if (
				distanceFromBottom <= LOAD_DISTANCE_FROM_BOTTOM_PX &&
				canLoadRef.current &&
				hasNextPageRef.current &&
				!isFetchingRef.current
			) {
				canLoadRef.current = false;
				fetchNextPageRef.current();
			}
		};

		rootEl.addEventListener("scroll", handleScroll, { passive: true });
		return () => rootEl.removeEventListener("scroll", handleScroll);
	}, [scrollRootId, enabled]);

	// Sentinel ref unused — loading is driven by scroll position only
	return () => undefined;
};
